import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

export const CodeBlock = ({ code, language, inline }: { code: string; language?: string; inline?: boolean }) => {
    const [copied, setCopied] = useState(false);

    if (inline) {
        return (
            <code className="rounded bg-muted px-[0.4rem] py-[0.2rem] font-mono text-sm">
                {code}
            </code>
        );
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative my-4 rounded-lg border bg-muted/50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/80">
                <span className="text-xs text-muted-foreground font-mono">{language || 'code'}</span>
                <button
                    onClick={handleCopy}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                    {copied ? 'Copied!' : 'Copy'}
                </button>
            </div>
            <pre className="overflow-x-auto p-4">
                <code className="font-mono text-sm whitespace-pre-wrap break-words">{code}</code>
            </pre>
        </div>
    );
};

export const markdownRenderers = {
    table: (props: any) => (
        // Wrap in a scroll container so a wide table scrolls within its own box
        // instead of stretching the parent (chat bubble / report dialog) sideways.
        <div className="my-6 w-full max-w-full overflow-x-auto">
            <Table>{props.children}</Table>
        </div>
    ),
    thead: (props: any) => (
        <TableHeader>{props.children}</TableHeader>
    ),
    tbody: (props: any) => (
        <TableBody>{props.children}</TableBody>
    ),
    tr: (props: any) => (
        <TableRow>{props.children}</TableRow>
    ),
    th: (props: any) => (
        <TableHead className="font-bold">{props.children}</TableHead>
    ),
    td: (props: any) => (
        <TableCell>{props.children}</TableCell>
    ),
    ul: (props: any) => (
        <ul className="pl-8 pr-8 list-disc w-full max-w-full space-y-2">{props.children}</ul>
    ),
    ol: (props: any) => (
        <ol className="pl-8 pr-8 list-decimal w-full max-w-full space-y-2">{props.children}</ol>
    ),
    li: (props: any) => (
        <li className="leading-7">{props.children}</li>
    ),
    p: (props: any) => (
        <p className="leading-7 [&:not(:first-child)]:mt-6">{props.children}</p>
    ),
    a: (props: any) => (
        <a
            href={props.href}
            target="_blank"
            rel="noreferrer"
            className="font-medium underline underline-offset-4 hover:text-primary break-words [overflow-wrap:anywhere]"
        >
            {props.children}
        </a>
    ),
    h1: (props: any) => (
        <h1 className="scroll-m-20 text-4xl font-bold tracking-tight">{props.children}</h1>
    ),
    h2: (props: any) => (
        <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight">{props.children}</h2>
    ),
    h3: (props: any) => (
        <h3 className="scroll-m-20 text-xl font-semibold tracking-tight">{props.children}</h3>
    ),
    strong: (props: any) => (
        <strong className="font-medium">{props.children}</strong>
    ),
}; 